import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button,Card,CardContent, Typography, Box } from "@mui/material";
import { fetchPaymentStatus } from "../services/booking";

const PayStatus = () => {
  const [status, setStatus] = useState("Checking payment...");
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingInfo, setBookingInfo] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refValue = params.get("ref");
    setRef(refValue);

    let interval;
    const checkStatus = () => {
      if (refValue) {
        fetchPaymentStatus(refValue)
          .then((data) => {
            if (!data || !data.status) {
              setStatus("FAILED");
              clearInterval(interval); // Stop polling
              return;
            }
            
            if (data.status === "SUCCESS" && data.details) {
              setStatus("SUCCESS");
              setBookingInfo(data.details);
              clearInterval(interval); // Stop polling
            } else if (data.status === "PENDING") {
              setStatus("PENDING");
            } else if (data.status === "PAYMENT_SUCCESS_BUT_BOOKING_FAILED") {
              setStatus("PAYMENT_SUCCESS_BUT_BOOKING_FAILED");
              setBookingInfo(data.details);
              clearInterval(interval); // Stop polling
            } else if (data.status === "FAILED_WITH_BOOKING") {
              setStatus("FAILED_WITH_BOOKING");
              setBookingInfo(data.details);
              clearInterval(interval); // Stop polling
            } else {
              setStatus("FAILED");
              clearInterval(interval); // Stop polling
            }
          })
          .catch(() => {
            setStatus("ERROR");
            clearInterval(interval); // Stop polling
          })
          .finally(() => setLoading(false));
      } else {
        setStatus("FAILED");
        clearInterval(interval); // Stop polling
        setLoading(false);
      }
    };
    checkStatus();
    interval = setInterval(checkStatus, 5000); // Poll every 5 sec
    return () => clearInterval(interval); // Cleanup on unmount
  }, [location.search]);

  return (
    <div className="flex flex-col justify-center items-center h-screen p-4">
      <div className="p-3">Booking Reference: {ref || "N/A"}</div>
      <div className="flex flex-col justify-center items-center">
        {loading ? (
          <div className="text-blue-500">⏳ Fetching payment status...</div>
        ) : (
          <>
            {status === "SUCCESS" && bookingInfo && bookingInfo.booking && bookingInfo.payment && (
              <>
                <Card className="p-2 mt-1 max-w-3xl w-full" sx={{ color: "#1f2937", boxShadow: "none" }}>
                  <CardContent sx={{ textAlign: "left", p:0, gap: 2, display: "flex", justifyContent:"center" }}>
                    {/* Booking Details Section */}
                    <div className={`p-2 rounded shadow-md ${bookingInfo.booking.status === "PAID" ? "bg-green-100" : "bg-orange-100"}`}>
                      <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
                          Booking Details
                      </Typography>
                      <Box sx={{ mx: "auto", p: 2, borderRadius: 1, bgcolor: "white", color: "#1f2937" }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography>Name:</Typography>
                          <Typography>{bookingInfo.booking.name}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography>Title:</Typography>
                          <Typography>{bookingInfo.booking.title}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={4}>
                          <Typography>Date:</Typography>
                          <Typography>{new Date(bookingInfo.booking.startTime).toLocaleString()}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography>Duration:</Typography>
                          <Typography>{bookingInfo.booking.duration / 3600} Hours</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" gap={2}>
                          <Typography>Amount:</Typography>
                          <Typography>{bookingInfo.payment.amount} {bookingInfo.payment.currency}</Typography>
                        </Box>
                      </Box>
                    </div>
                  </CardContent>
                </Card>
                <div className="text-green-600 text-center">✅ Payment successful!</div>
              </>
            )}
            {status === "PENDING" && <div className="text-yellow-500">🚀 Payment is processing...</div>}
            {status === "PAYMENT_SUCCESS_BUT_BOOKING_FAILED" && bookingInfo && bookingInfo.payment && ( 
              <>
              <Card className="p-2 mt-1 max-w-3xl w-full" sx={{ color: "#1f2937", boxShadow: "none" }}>
                <CardContent sx={{ textAlign: "left", p:0, gap: 2, display: "flex", justifyContent:"center" }}>
                  {/* Payment Details Section */}
                  <div className={`p-2 rounded shadow-md ${bookingInfo.payment.status === "SUCCESS" ? "bg-green-100" : "bg-orange-100"}`}>
                    <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
                        Payment Details
                    </Typography>
                    <Box sx={{ mx: "auto", p: 2, borderRadius: 1, bgcolor: "white", color: "#1f2937" }}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Name:</Typography>
                        <Typography>{bookingInfo.payment.cardHolderName}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography>Amount:</Typography>
                        <Typography>{bookingInfo.payment.amount} LKR</Typography>
                      </Box>
                    </Box>
                  </div>
                </CardContent>
              </Card>
              <div className="text-red-600 text-center">
                ⚠️ Your payment was received, but we were unable to place the booking due to a system issue.
                <div className="mt-2">Please contact us at +94 77 760 6279</div>
              </div>
            </>
            )}
            {status === "FAILED_WITH_BOOKING" && bookingInfo && bookingInfo.booking && (
              <>
              <Card className="p-2 mt-1 max-w-3xl w-full" sx={{ color: "#1f2937", boxShadow: "none" }}>
                <CardContent sx={{ textAlign: "left", p:0, gap: 2, display: "flex", justifyContent:"center" }}>
                  {/* Booking Details Section */}
                  <div className={`p-2 rounded shadow-md ${bookingInfo.booking.status === "PAID" ? "bg-green-100" : "bg-orange-100"}`}>
                    <Typography variant="h6" sx={{ textAlign: "center", mb: 1 }}>
                        Booking Details
                    </Typography>
                    <Box sx={{ mx: "auto", p: 2, borderRadius: 1, bgcolor: "white", color: "#1f2937" }}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Name:</Typography>
                        <Typography>{bookingInfo.booking.name}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Title:</Typography>
                        <Typography>{bookingInfo.booking.title}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" gap={4}>
                        <Typography>Date:</Typography>
                        <Typography>{new Date(bookingInfo.booking.startTime).toLocaleString()}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Duration:</Typography>
                        <Typography>{bookingInfo.booking.duration / 3600} Hours</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography>Amount:</Typography>
                        <Typography>{bookingInfo.booking.price} LKR</Typography>
                      </Box>
                    </Box>
                  </div>
                </CardContent>
              </Card>
              <div className="text-red-600 text-center">
                ⚠️ Your payment was received, but we were unable to verify due to a system issue.
                <div className="mt-2">Please contact us at +94 77 760 6279</div>
              </div>
            </>
            )}
            {status === "FAILED" && (
              <div className="text-red-600 text-center">
                ❌ Payment not verified. No booking was placed.
              </div>
            )}
            {status === "ERROR" && (
              <div className="text-red-600 text-center">
                ⚠️ Unable to retrieve payment status, due to poor network connectivity.
                <div className="mt-2"> Please try reloading the page,</div>
                <div className="mt-0"> Or contact us at +94 77 760 6279</div>
              </div>
            )}
          </>
        )}
        {ref && (
          <div className="text-gray-500 text-center mt-2">
            🔍 Please save your reference number for any inquiries or updates.
          </div>
        )}
      </div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate("/")}
        sx={{ marginTop: "20px" }}
      >
        Back to Home
      </Button>
    </div>
  );
};

export default PayStatus;
